import { arrayMove } from '@dnd-kit/sortable';
import { useLocalStorage } from '@mantine/hooks';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { OmniModal } from 'components/OmniModal/OmniModal';
import React, { Fragment, memo, useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { TSessionConfigData, useCosmos } from '../../../providers/contexts/CosmosContext';
import { IconHelper } from '../../generic/IconHelper';
import { NxtImport } from '../../ExportImport/NxtImport/NxtImport';
import { ImportButton } from '../../ExportImport/NxtImport/components/ImportButton';
import { getAppViaLocation } from '../LeftNav/leftNavFunctions';
import {
  ISearchState,
  IViewState,
  PinnedTemplate,
  PlusMenuKey,
  PlusMenuRecord,
  PlusNavObj,
} from './types';
import ComponentViewWrapper from './views/ComponentViewWrapper';
import MainViewContent from './views/MainViewContent';
import { ComponentWithProps, templateToPlusNavObj } from './templateNavItem';
import {
  checkBroadcastFullAccessPermission,
  checkBroadcastSendNotificationsPermission,
  checkCreateAssetsPermission,
  checkSiteAdminPermission,
  checkUserAccountPermission,
} from 'utility/permissions';
import { useAnalytics } from 'providers/contexts/analytics/AnalyticsContext';
import { useBlackbirdLink } from 'providers/hooks/useBlackbirdLink';
import { hasBetaProgram } from 'utility/experiments';
import { GetUserMeData } from 'utility/types/dataProvider';
import { useAssetQuickLaunch } from 'components/editorial/AssetQuickLaunch/useAssetQuickLaunch';
import { extractAssetTypeFromPath } from 'components/editorial/AssetQuickLaunch/utils';
import type { RecentStoryTemplate } from 'components/editorial/AssetQuickLaunch/useRecentStoryTemplates';
import { useCreateFromTemplate } from 'components/Minimenus/hooks/useCreateFromTemplate';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { buildQueryUrl } from 'components/Search/SearchContextProvider';
import { mergeDeep } from 'remeda';
import { sendDateDesc } from 'components/Search/const';

// This is a test component to show an example of how the action/component prop works
// and can be safely removed, Leaving in the comments for future reference
// const TestDisplayComponent = () => {
//   return (
//     <Stack>
//       {Array.from({ length: 50 }).map((_, i) => (
//         <Typography
//           key={i}
//           sx={{ p: 1, color: 'gray', fontSize: 12 }}
//         >
//           Test action/component Area
//         </Typography>
//       ))}
//     </Stack>
//   );
// };

type GetPlusMenuItemsParams = {
  activeSite: string;
  t: TFunction;
  sessionConfig?: TSessionConfigData;
  userInfo?: GetUserMeData;
};

export function getPlusMenuItems({
  activeSite,
  t,
  sessionConfig,
  userInfo,
}: GetPlusMenuItemsParams): PlusMenuRecord {
  const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
  const { granted: userEditorialCreate } = checkCreateAssetsPermission(sessionConfig);
  const inBetaProgram = hasBetaProgram(sessionConfig, userInfo);

  const hasCreateEditorial = Boolean(userIsAdmin || userEditorialCreate);

  const { granted: hasUserAccountPerm } = checkUserAccountPermission(sessionConfig);
  const hasCreateUserPerms = Boolean(userIsAdmin || hasUserAccountPerm);

  // Notifier / Broadcast permissions
  const { granted: userHasFullBroadcast } =
    checkBroadcastFullAccessPermission(sessionConfig);
  const { granted: userHasSendBroadcast } =
    checkBroadcastSendNotificationsPermission(sessionConfig);
  const canCreateNotifierPost = Boolean(
    userIsAdmin || userHasSendBroadcast || userHasFullBroadcast,
  );

  // Build notifier create URL with default sort dir
  const notifierParams = mergeDeep(sendDateDesc, { openModal: 'create' });
  const notifierCreateUrl = buildQueryUrl({
    baseUrl: `/${activeSite}/notifier/search`,
    newParams: notifierParams,
  });

  const menuItems: PlusMenuRecord = {
    editorial: {
      metadata: {
        header: 'Editorial',
      },
      items: {
        // template: {
        //   key: 'editorial-template',
        //   label: 'Template (test)',
        //   icon: ComponentWithProps(IconHelper, { iconType: 'template' }),
        //   action: 'display',
        //   component: TestDisplayComponent,
        //   keywords: ['template', 'asset', 'editorial'],
        // },
        article: {
          key: 'editorial-article',
          label: 'Article',
          path: `/${activeSite}/editorial/create/article/`,
          action: 'navigate',
          icon: ComponentWithProps(IconHelper, { iconType: 'article' }),
          keywords: ['article', 'asset', 'editorial'],
          hidden: !hasCreateEditorial,
        },
        image: {
          key: 'editorial-image',
          label: 'Image',
          path: `/${activeSite}/editorial/create/image/`,
          action: 'navigate',
          icon: ComponentWithProps(IconHelper, { iconType: 'image' }),
          keywords: ['image', 'asset', 'editorial'],
          hidden: !hasCreateEditorial,
        },
        collection: {
          key: 'editorial-collection',
          label: 'Collection',
          path: `/${activeSite}/editorial/create/collection/`,
          action: 'navigate',
          icon: ComponentWithProps(IconHelper, { iconType: 'collection' }),
          keywords: ['collection', 'asset', 'editorial'],
          hidden: !hasCreateEditorial,
        },
        audio: {
          key: 'editorial-audio',
          label: 'Audio',
          path: `/${activeSite}/editorial/create/audio/`,
          action: 'navigate',
          icon: ComponentWithProps(IconHelper, { iconType: 'audio' }),
          keywords: ['audio', 'asset', 'editorial'],
          hidden: !hasCreateEditorial,
        },
        file: {
          key: 'editorial-file',
          label: 'File',
          path: `/${activeSite}/editorial/create/file/`,
          action: 'navigate',
          icon: ComponentWithProps(IconHelper, { iconType: 'file' }),
          keywords: ['file', 'asset', 'editorial'],
          hidden: !hasCreateEditorial,
        },
        html: {
          key: 'editorial-html',
          label: 'HTML',
          path: `/${activeSite}/editorial/create/html/`,
          action: 'navigate',
          icon: ComponentWithProps(IconHelper, { iconType: 'html' }),
          keywords: ['html', 'asset', 'editorial'],
          hidden: !hasCreateEditorial,
        },
        link: {
          key: 'editorial-link',
          label: 'Link',
          path: `/${activeSite}/editorial/create/link/`,
          action: 'navigate',
          icon: ComponentWithProps(IconHelper, { iconType: 'link' }),
          keywords: ['link', 'asset', 'editorial'],
          hidden: !hasCreateEditorial,
        },
        poll: {
          key: 'editorial-poll',
          label: 'Poll',
          path: `/${activeSite}/editorial/create/poll/`,
          action: 'navigate',
          icon: ComponentWithProps(IconHelper, { iconType: 'poll' }),
          keywords: ['poll', 'asset', 'editorial'],
          hidden: !hasCreateEditorial,
        },
        table: {
          key: 'editorial-table',
          label: 'Table',
          path: `/${activeSite}/editorial/create/table/`,
          action: 'navigate',
          icon: ComponentWithProps(IconHelper, { iconType: 'table' }),
          keywords: ['table', 'asset', 'editorial'],
          hidden: !hasCreateEditorial,
        },
        video: {
          key: 'editorial-video',
          label: 'Video',
          path: `/${activeSite}/editorial/create/video/`,
          action: 'navigate',
          icon: ComponentWithProps(IconHelper, { iconType: 'video' }),
          keywords: ['video', 'asset', 'editorial'],
          hidden: !hasCreateEditorial,
        },
      },
    },
    user: {
      metadata: {
        header: 'Community',
      },
      items: {
        create: {
          key: 'user-create',
          label: 'Account',
          path: `/${activeSite}/user/create`,
          action: 'navigate',
          icon: ComponentWithProps(IconHelper, { iconType: 'user' }),
          keywords: ['account', 'user', 'admin', 'community'],
          hidden: !hasCreateUserPerms,
        },
      },
    },
    notifier: {
      metadata: {
        header: t('blox.notifier.label'),
      },
      items: {
        create_post: {
          key: 'create-notifier-post',
          label: t('blox.notifier.notifier_post'),
          showTitle: true,
          action: 'navigate',
          path: notifierCreateUrl,
          icon: ComponentWithProps(IconHelper, { iconType: 'notifier' }),
          keywords: ['notifier', 'create', 'broadcast', 'notification', 'post'],
          hidden: !canCreateNotifierPost,
        },
      },
    },
    // adds batch shortcut to 'show all/pinned' apps area...
    batch: {
      metadata: {
        header: 'Import',
      },
      items: {
        batch: {
          key: 'nxt-import',
          label: `Batch Upload/Import`,
          showTitle: true,
          path: `/${activeSite}/*`,
          action: 'display',
          icon: ComponentWithProps(IconHelper, { iconType: 'import' }),
          component: NxtImport,
          keywords: ['upload', 'create', 'asset', 'editorial', 'batch', 'import'],
          hidden: !inBetaProgram && !hasCreateEditorial,
        },
      },
    },
    blackbird: {
      metadata: {
        header: 'Live Clip (Blackbird)',
      },
      items: {
        blackbird: {
          key: 'blackbird',
          label: 'Live Clip (Blackbird)',
          showTitle: true,
          action: 'callback',
          icon: ComponentWithProps(IconHelper, { iconType: 'movieedit' }),
          keywords: ['upload', 'create', 'asset', 'editorial', 'video', 'edit'],
        },
      },
    },
  };
  return menuItems;
}

// typeguard helper
export function isPlusMenuKey(key: string): key is PlusMenuKey {
  return ['editorial', 'user', 'batch', 'notifier'].includes(key);
}

const initialViewState: IViewState = { view: 'main', viewComponentKey: null };
const initialSearchState: ISearchState = { query: '', showAll: false };
const initialEditMode: boolean = false;

export const PlusButton = memo(() => {
  const { t } = useTranslation();
  const { activeSite, sessionConfig, userInfo } = useCosmos();
  const location = useLocation();
  const navigate = useNavigate();
  const { trackEvent, menuIds } = useAnalytics();
  const searchFieldRef = React.useRef<HTMLInputElement>(null);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [viewState, setViewState] = useState<IViewState>(initialViewState);
  const [pinnedApps, setPinnedApps] = useLocalStorage<string[]>({
    key: 'createPinnedApps', // Local storage key (ordered app + template pin keys)
    defaultValue: [],
  });
  // Snapshot of each pinned template's metadata (plus the site it was pinned on).
  // Templates aren't in a static list like apps, so we persist enough to render
  // and re-create them even when they're outside the current recent/search set.
  const [pinnedTemplates, setPinnedTemplates] = useLocalStorage<PinnedTemplate[]>({
    key: 'createPinnedTemplates',
    defaultValue: [],
  });

  const [searchState, setSearchState] = useState(initialSearchState);
  const [editMode, setEditMode] = useState(initialEditMode);
  const [creatingTemplateId, setCreatingTemplateId] = useState<string | null>(null);

  const {
    openQuickLaunch,
    modal: quickLaunchModal,
    isQuickLaunchAssetType,
  } = useAssetQuickLaunch();

  // The Story Templates section is an editorial-create feature, so gate it on the
  // same permission that hides Article/Image/etc. for non-creators.
  const canCreateEditorial = useMemo(() => {
    const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
    const { granted: userEditorialCreate } = checkCreateAssetsPermission(sessionConfig);
    return Boolean(userIsAdmin || userEditorialCreate);
  }, [sessionConfig]);

  // NOTE: There is intentionally no template fetching here. Each search-backed
  // result group owns its own data fetch (StoryTemplatesSection runs its own
  // useRecentStoryTemplates off the shared top-bar query). Keeping the fetches in
  // their result components means this menu never turns into a pile of per-type
  // API calls as we add more searchable types: the search bar routes one query,
  // and only the groups actually on screen fetch. See StoryTemplatesSection.

  const { createFromTemplate } = useCreateFromTemplate({
    invalidateQueries: true,
    // Navigate in the same tab, matching the quick launch template flow.
    openInNewTab: false,
    onSuccess: () => {
      setCreatingTemplateId(null);
    },
    onError: () => {
      setCreatingTemplateId(null);
    },
  });

  const showAllApps = Boolean(searchState.showAll || searchState.query);

  // Turn the stored template snapshots into PlusNavObj items so the unified
  // Pinned grid can render and reorder them alongside pinned apps. Only this
  // site's pins are surfaced: a template uuid belongs to one site, so showing
  // another site's pin here would create-from-template into an "asset not found".
  // Off-site pins stay in storage and reappear when that site is active again.
  const pinnedTemplateItems = useMemo<PlusNavObj[]>(
    () =>
      pinnedTemplates
        .filter((tpl) => tpl.site === activeSite)
        .map((tpl) =>
          templateToPlusNavObj(
            tpl,
            t('blox.editorial.templates.createMenu.untitled', 'Untitled Template'),
          ),
        ),
    [pinnedTemplates, activeSite, t],
  );

  const menuItems = useMemo(
    () => getPlusMenuItems({ activeSite, sessionConfig, userInfo, t }),
    [activeSite, sessionConfig, userInfo, t],
  );

  // Initialize the hook but don't enable automatic fetching
  const { tryBlackbirdLink } = useBlackbirdLink({ enabled: false });

  const handlePopoverOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    // Analytics.
    trackEvent({
      event: 'Admin Menu Opened',
      data: {
        opened_menu_id: menuIds.global['Grid Header Create Asset']?.uuid,
        opened_menu_name: 'Grid Header Create Asset',
      },
    });
    // focus search input on open
    setTimeout(() => {
      searchFieldRef.current?.focus();
    }, 0);
  };

  const handlePopoverClose = useCallback(() => {
    setAnchorEl(null);
    setViewState(initialViewState);
    setSearchState(initialSearchState);
    // Analytics.
    trackEvent({
      event: 'Admin Menu Closed',
      data: {
        closed_menu_id: menuIds.global['Grid Header Create Asset']?.uuid,
        closed_menu_name: 'Grid Header Create Asset',
      },
    });
  }, [trackEvent, menuIds]);

  // Single pin handler for apps and templates alike (used by Pinned, Recommended,
  // and the Story Templates section). It receives the whole nav item, so for a
  // template it can read item.template and persist a small snapshot
  // ({uuid,title,type}) without needing the fetched template list (which now
  // lives inside StoryTemplatesSection). The snapshot lets a pinned template
  // still render in the Pinned grid when it's outside the recent/searched set.
  // Apps carry no item.template, so they only toggle the ordered key array.
  const handlePinToggle = (item: PlusNavObj) => {
    const key = item.key;
    const currentlyPinned = pinnedApps.includes(key);
    // Guard the add against the key already being present: a fast double-click on
    // the pin would otherwise see a stale `currentlyPinned` twice and append the
    // same key twice, producing a duplicate (and a duplicate dnd-sortable id).
    setPinnedApps((prevPinnedApps) =>
      currentlyPinned
        ? prevPinnedApps.filter((appKey) => appKey !== key)
        : prevPinnedApps.includes(key)
          ? prevPinnedApps
          : [...prevPinnedApps, key],
    );

    const template = item.template;
    if (!template) {
      return;
    }
    setPinnedTemplates((prev) => {
      if (currentlyPinned) {
        return prev.filter((tpl) => tpl.uuid !== template.uuid);
      }
      if (prev.some((tpl) => tpl.uuid === template.uuid)) {
        return prev;
      }
      // Record the site so this pin is only surfaced back on the same site.
      return [
        ...prev,
        {
          uuid: template.uuid,
          title: template.title,
          type: template.type,
          site: activeSite,
        },
      ];
    });
  };

  const handleReorderApps = (oldIndex: number, newIndex: number) => {
    setPinnedApps((prevPinnedApps) => {
      const updatedPinnedApps = arrayMove(prevPinnedApps, oldIndex, newIndex);
      return updatedPinnedApps;
    });
  };

  // Create a new asset from a story template and open the editor immediately.
  // No intermediate modal: the template's structure and metadata are inherited.
  const handleTemplateClick = useCallback(
    (template: RecentStoryTemplate) => {
      if (!template?.uuid || creatingTemplateId) {
        return;
      }
      setCreatingTemplateId(template.uuid);
      trackEvent({
        event: 'Create From Template',
        data: {
          template_id: template.uuid,
          template_type: template.type,
          source: 'Grid Header Create Asset',
        },
      });
      createFromTemplate({ templateId: template.uuid }).catch((error) => {
        console.error('[PlusButton] Create from template failed:', error);
        setCreatingTemplateId(null);
      });
      handlePopoverClose();
    },
    [createFromTemplate, creatingTemplateId, handlePopoverClose, trackEvent],
  );

  const handleItemClick = useCallback(
    (event: React.SyntheticEvent<HTMLElement>, item: PlusNavObj) => {
      event.preventDefault();

      switch (item?.action) {
        case 'navigate': {
          if (item?.path) {
            // Skip modal if already on this create path
            const isAlreadyOnPath =
              location.pathname === item.path ||
              location.pathname === item.path.replace(/\/$/, '');
            if (isAlreadyOnPath) {
              handlePopoverClose();
              break;
            }

            const assetType = extractAssetTypeFromPath(item.path);
            if (assetType && isQuickLaunchAssetType(assetType)) {
              handlePopoverClose();
              openQuickLaunch({
                assetType,
                assetLabel: item.label,
                navigatePath: item.path,
              });
              break;
            }
            handlePopoverClose();
            navigate(`${item.path}`);
          }
          break;
        }
        case 'display': {
          if (item?.component) {
            setViewState({ view: 'component', viewComponentKey: item.key });
          }
          break;
        }
        case 'callback': {
          if (item.key === 'blackbird') {
            tryBlackbirdLink(true);
          }
          break;
        }
        case 'template': {
          if (item.template) {
            handleTemplateClick(item.template);
          }
          break;
        }
        default: {
          console.warn(`Unhandled action: ${item.action}`);
          break;
        }
      }
    },
    [
      handlePopoverClose,
      location.pathname,
      navigate,
      openQuickLaunch,
      tryBlackbirdLink,
      isQuickLaunchAssetType,
      handleTemplateClick,
    ],
  );

  const handleBackClick = useCallback(() => {
    setSearchState(initialSearchState);
    setViewState(initialViewState);
  }, []);

  const handleShowAllAppsClick = useCallback(() => {
    setSearchState({ query: '', showAll: true });
    setViewState(initialViewState);
  }, []);

  const handleNxtImportView = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setSearchState(initialSearchState);
      setViewState({ view: 'component', viewComponentKey: 'nxt-import' });
    },
    [],
  );

  const selectedComponent = useMemo(() => {
    if (viewState.viewComponentKey) {
      return Object.values(menuItems)
        .flatMap((record) => Object.values(record.items))
        .find((item) => item.key === viewState.viewComponentKey);
    }
  }, [viewState.viewComponentKey, menuItems]);

  const currentAppStr = useMemo(() => {
    return getAppViaLocation(location.pathname, activeSite);
  }, [location.pathname, activeSite]);

  const searchResults = useMemo(() => {
    return Object.values(menuItems)
      .flatMap((record) => Object.values(record.items))
      .filter((item) => {
        const query = searchState.query.toLowerCase();
        // Match label or keywords array
        return (
          item.label.toLowerCase().includes(query) ||
          item.keywords?.some((keyword) => keyword.toLowerCase().includes(query))
        );
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [searchState.query, menuItems]);

  const allApps = useMemo(() => {
    return Object.values(menuItems)
      .flatMap((record) => Object.values(record.items))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [menuItems]);

  const recommendedApps = useMemo(() => {
    // root home - show all applications in recommended
    if (currentAppStr === 'root') {
      return allApps;
    }
    // typeguard - key must be in PlusMenuKey to proceed
    if (!isPlusMenuKey(currentAppStr)) {
      return [];
    }
    // Typeguarded with verified key
    const items = menuItems?.[currentAppStr]?.items;
    // guard for items existence
    if (!items) {
      return [];
    }
    // sort and return
    const sortedItems = Object.values(items).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
    return sortedItems;
  }, [allApps, currentAppStr, menuItems]);

  return (
    <Fragment>
      <ImportButton
        id="plus-batch-open-button"
        testId="plus-batch-open-button"
        color="primary"
        onClickFunction={handlePopoverOpen}
        tooltip="Create"
        icon={<AddCircleIcon />}
      />
      <OmniModal
        anchorEl={anchorEl}
        handleClose={handlePopoverClose}
        sx={{
          '> .MuiPaper-root': {
            width: 'calc(100vw - 16px)',
            maxWidth: '600px',
            boxSizing: 'border-box',
            // Grow taller so all sections (incl. Story Templates + Search All
            // link) fit. The popover opens ~52px below the top, so cap the height
            // to the space that remains beneath it to avoid clipping the bottom.
            height: '85vh',
            maxHeight: 'calc(100vh - 68px)',
          },
        }}
      >
        {viewState.view === 'main' ? (
          <MainViewContent
            allApps={allApps}
            currentAppStr={currentAppStr}
            editMode={editMode}
            handleBackClick={handleBackClick}
            handleNxtImportView={handleNxtImportView}
            handleClose={handlePopoverClose}
            handleItemClick={handleItemClick}
            handlePinToggle={handlePinToggle}
            handleReorderApps={handleReorderApps}
            handleShowAllAppsClick={handleShowAllAppsClick}
            isOpen={Boolean(anchorEl)}
            pinnedApps={pinnedApps}
            pinnedTemplateItems={pinnedTemplateItems}
            recommendedApps={recommendedApps}
            searchResults={searchResults}
            searchState={searchState}
            setEditMode={setEditMode}
            setSearchState={setSearchState}
            setViewState={setViewState}
            showAllApps={showAllApps}
            searchFieldRef={searchFieldRef}
            showTemplates={canCreateEditorial}
          />
        ) : selectedComponent ? (
          <ComponentViewWrapper
            SelectedComponent={selectedComponent}
            handleBackClick={handleBackClick}
            handleClose={handlePopoverClose}
          />
        ) : null}
      </OmniModal>
      {quickLaunchModal}
    </Fragment>
  );
});
PlusButton.displayName = 'PlusButton';
